import React, { useEffect, useState, useContext, useCallback } from 'react';
import { string, func, number } from 'prop-types';
import { ExperienceContext, useStoreId } from '@thd-nucleus/experience-context';
import Script from 'react-load-script';
import classNames from 'classnames';
import { useProductDeprecated } from '@thd-nucleus/data-sources/react/dataModel/migration';
import { useProduct } from '~/@thd-nucleus/data-sources';
import { Options } from './Options';
import { Message } from './Message';
import { Frequency } from './Frequency';
import { Events } from './Events';
import { dataModel } from './dataModel';
import {
  update,
  init,
  setOgFrequency,
  handleError
} from '../order-groove/order-groove';
import './subscribe-and-save.scss';

const SubscribeAndSave = ({ itemId, quantity, fulfillment, onChange }) => {
  const DEFAULT_FREQUENCY = '6';
  const DEFAULT_DISCOUNT = '5';
  const FULFILLMENT_METHOD_STH = 'ShipToHome';
  const [frequency, setFrequency] = useState('6_3');
  const [newFrequency, setNewFrequency] = useState('');
  const [iQuantity, setIQuantity] = useState(quantity);
  const [optIn, setOptIn] = useState(false);
  const { channel, hosts: { orderGroove } } = useContext(ExperienceContext);
  const storeId = useStoreId();
  const ogRef = useCallback((node) => {
    if (node !== null) {
      if (node.querySelector('.og-widget')) setOgFrequency({ itemId, frequency });
    }
  });
  const classes = classNames({
    'subscribe-and-save': fulfillment === FULFILLMENT_METHOD_STH,
    'subscribe-and-save--disabled': fulfillment !== FULFILLMENT_METHOD_STH
  });

  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('subscribe-and-save.ready'); }, []);

  const isShipToHome = (product) => {
    const deliveryService = (product?.fulfillment?.fulfillmentOptions || [])
      .find((option) => option.type === 'delivery');
    const shippingFulfillment = (deliveryService?.services || []).find((service) => service.type === 'sth');

    return !!shippingFulfillment;
  };

  const scriptErrorHandler = () => {
    handleError(new Error('OG script failed to load.'));
  };

  const scriptLoadHandler = () => {
    init({ itemId, quantity: iQuantity });
  };

  const handleQuantityChange = (qty) => {
    setIQuantity(qty);
  };

  const domains = ['pricing', 'subscription', 'fulfillment'];
  const { data, loading, error } = useProductDeprecated({
    itemId,
    storeId,
    domains,
    batchDomains: false,
    ssr: false,
    skip: storeId === '8119'
  }, 'clientOnlyProduct');

  if (!data || loading || (error && !data)) {
    return null;
  }

  const { product } = data;

  if (!product) return null;

  const { defaultfrequency, discountPercentage, subscriptionEnabled } = product?.subscription || {};
  const { value } = product?.pricing || {};
  const shipToHome = isShipToHome(product);

  if (!subscriptionEnabled || !shipToHome || !value) return null;

  const getOgFrequencyFromValue = (ogValue) => {
    const NO_FREQUENCY = '0_0';
    const frequencyMap = {
      1: '1_3',
      2: '2_3',
      3: '3_3',
      4: '4_3',
      5: '5_3',
      6: '6_3',
      12: '12_3'
    };

    return ogValue ? frequencyMap[ogValue] : NO_FREQUENCY;
  };

  const handleFrequencyChange = (frequencyValue) => {
    setFrequency(frequencyValue);
    update({ itemId, quantity: iQuantity, frequency: frequencyValue });
    setOptIn(true);
    onChange({ frequency: frequencyValue, isOptIn: true });
  };

  const handleOptionChange = (optionsValue) => {
    const ogFrequency = getOgFrequencyFromValue(defaultfrequency);

    if (!newFrequency) {
      setNewFrequency(ogFrequency);
      setFrequency(ogFrequency);
      setOptIn(optionsValue);
      onChange({ frequency: ogFrequency, isOptIn: optionsValue });

    } else {
      setOptIn(optionsValue);
      onChange({ frequency, isOptIn: optionsValue });
    }
  };

  return (
    <>
      <Events itemId={itemId} subscribed={optIn} frequency={frequency} onQuantityChange={handleQuantityChange} />
      <div className={classes} data-testid="subscribe-and-save">
        <div id="subscribe-and-save-options">
          <Options
            price={value}
            subscribed={optIn}
            onChange={handleOptionChange}
            discount={discountPercentage || DEFAULT_DISCOUNT}
            disabled={fulfillment !== FULFILLMENT_METHOD_STH}
          />
        </div>
        <div id="subscribe-and-save-message">
          <Message
            channel={channel}
            discount={discountPercentage || DEFAULT_DISCOUNT}
            disabled={fulfillment !== FULFILLMENT_METHOD_STH}
          />
        </div>
        <div id="subscribe-and-save-frequency">
          <Frequency
            defaultFrequency={defaultfrequency || DEFAULT_FREQUENCY}
            onChange={handleFrequencyChange}
            disabled={fulfillment !== FULFILLMENT_METHOD_STH}
          />
        </div>
      </div>
      <div className="og-offer" data-og-module="pdp_nocontent" data-og-product="*" ref={ogRef} />
      <Script
        url={`${orderGroove}/379dc8f09a4311e7806bbc764e106cf4/main.js`}
        onError={scriptErrorHandler}
        onLoad={scriptLoadHandler}
      />
    </>
  );
};

SubscribeAndSave.displayName = 'SubscribeAndSave';

SubscribeAndSave.propTypes = {
  itemId: string.isRequired,
  onChange: func.isRequired,
  /** currently selected fulfillment option */
  fulfillment: string.isRequired,
  /** currently selected quantity */
  quantity: number
};

SubscribeAndSave.defaultProps = {
  quantity: 1
};

SubscribeAndSave.dataModel = dataModel;

export { SubscribeAndSave };
